import {
  clearVerifyEmailSession,
  getVerifyEmailSession,
  saveVerifyEmailSession
} from './verifyEmailSession';
import {
  EMAIL_VERIFICATION_RESEND_DELAY_SECONDS,
  getShouldInvalidateUserProfile,
  getVerifyEmailStateFromErrorCode
} from './verifyEmail';
import {
  getEmailVerifyErrorData,
  resendVerifyEmail,
  verifyEmail
} from '../api';
import { getRetryAfterSeconds } from '@/shared/lib/getRetryAfterSeconds';
import { useAuthStore } from './authStore';
import { useCountdown } from '@/shared/hooks/useCountdown';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userProfileQueryKey } from '@/features/Profile/model/queryKeys';
import { VerifyEmailState } from './types';

interface UseVerifyEmailFlowParams {
  onNavigateToLogin: () => void;
  onNavigateToMain: () => void;
  onRemoveVerificationToken: () => void;
  pathname: string;
  verificationToken: string;
}

interface UseVerifyEmailFlowResult {
  emailMasked: string | null;
  isGoToAppDisabled: boolean;
  isLoading: boolean;
  isResending: boolean;
  onGoToApp: () => Promise<void>;
  onResend: () => void;
  resendSecondsLeft: number;
  state: VerifyEmailState;
}

export const useVerifyEmailFlow = (params: UseVerifyEmailFlowParams): UseVerifyEmailFlowResult => {
  const {
    onNavigateToLogin,
    onNavigateToMain,
    onRemoveVerificationToken,
    pathname,
    verificationToken
  } = params;

  const [restoredSession] = useState(() => (!verificationToken ? getVerifyEmailSession() : null));

  const [emailMasked, setEmailMasked] = useState<string | null>(() => restoredSession?.emailMasked ?? null);
  const [isVerifying, setIsVerifying] = useState(Boolean(verificationToken));
  const [verifyEmailState, setVerifyEmailState] = useState<VerifyEmailState>(
    () => restoredSession?.state ?? 'not_found'
  );

  const isAuthInitialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userId = useAuthStore((state) => state.tokenData?.user_id ?? null);
  const queryClient = useQueryClient();

  const hasSubmittedRef = useRef(false);

  const {
    secondsLeft: resendSecondsLeft,
    startCountdown: startResendCountdown
  } = useCountdown();

  const {
    mutate: mutateVerifyEmail
  } = useMutation({
    mutationFn: verifyEmail,
    mutationKey: ['verifyEmail', pathname],
    onError: (error) => {
      const errorData = getEmailVerifyErrorData(error);
      const nextState = getVerifyEmailStateFromErrorCode(errorData?.error_code ?? null);

      if (nextState === 'already_confirmed') {
        saveVerifyEmailSession({
          emailMasked: null,
          state: 'already_confirmed'
        });
      } else {
        clearVerifyEmailSession();
      }

      setEmailMasked(nextState === 'expired' ? errorData?.email_masked ?? null : null);
      setVerifyEmailState(nextState);
      if (nextState !== 'expired') {
        onRemoveVerificationToken();
      }
    },
    onSettled: () => {
      setIsVerifying(false);
    },
    onSuccess: (data) => {
      const nextEmailMasked = data.email_masked ?? null;

      saveVerifyEmailSession({
        emailMasked: nextEmailMasked,
        state: 'confirmed'
      });
      setEmailMasked(nextEmailMasked);
      setVerifyEmailState('confirmed');
      onRemoveVerificationToken();
    }
  });

  const {
    isPending: isPendingResendVerifyEmail,
    mutate: mutateResendVerifyEmail
  } = useMutation({
    mutationFn: resendVerifyEmail,
    mutationKey: ['resendVerifyEmail', pathname],
    onError: (error) => {
      const retrySeconds = getRetryAfterSeconds(error);

      if (retrySeconds) {
        startResendCountdown(retrySeconds);
      }
    },
    onSuccess: (data) => {
      setEmailMasked(data.email_masked ?? null);
      startResendCountdown(EMAIL_VERIFICATION_RESEND_DELAY_SECONDS);
      setVerifyEmailState('resent');
    }
  });

  useEffect(() => {
    if (!verificationToken || hasSubmittedRef.current) return;

    hasSubmittedRef.current = true;
    clearVerifyEmailSession();
    mutateVerifyEmail({
      token: verificationToken
    });
  }, [mutateVerifyEmail, verificationToken]);

  const onGoToApp = async (): Promise<void> => {
    if (!isAuthInitialized) return;

    if (!isAuthenticated) {
      onNavigateToLogin();
      return;
    }

    if (userId && getShouldInvalidateUserProfile(verifyEmailState)) {
      await queryClient.invalidateQueries({
        queryKey: userProfileQueryKey(userId)
      });
    }

    onNavigateToMain();
  };

  const onResendVerifyEmail = (): void => {
    if (!verificationToken || isPendingResendVerifyEmail || resendSecondsLeft) return;

    mutateResendVerifyEmail({
      token: verificationToken
    });
  };

  return {
    emailMasked,
    isGoToAppDisabled: !isAuthInitialized,
    isLoading: isVerifying,
    isResending: isPendingResendVerifyEmail,
    onGoToApp,
    onResend: onResendVerifyEmail,
    resendSecondsLeft,
    state: verifyEmailState
  };
};